import React, { useState, useEffect } from 'react';
import {
  SettingDefinition,
  CreateSettingDefinitionRequest,
  UpdateSettingDefinitionRequest,
  SETTING_DATA_TYPES,
  DATA_TYPE_LABELS,
  SettingCategory,
  SettingDataType
} from '../../types/SettingDefinition';
import { settingDefinitionApi } from '../../services/settingDefinitionApi';

const SettingDefinitionManagement: React.FC = () => {
  const [definitions, setDefinitions] = useState<SettingDefinition[]>([]);
  const [categories, setCategories] = useState<SettingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Kategori-administrasjon
  const [showCategoryAdmin, setShowCategoryAdmin] = useState(false);
  const [newCategory, setNewCategory] = useState<SettingCategory>({ categoryKey: '', displayName: '', displayOrder: 100, isActive: true });
  const [editingCategoryKey, setEditingCategoryKey] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<SettingCategory | null>(null);

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Edit state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateSettingDefinitionRequest | null>(null);

  // Create state
  const [isCreating, setIsCreating] = useState(false);
  const [newDefinition, setNewDefinition] = useState<CreateSettingDefinitionRequest>({
    settingKey: '',
    displayName: '',
    description: '',
    category: 'general',
    dataType: 'String',
    defaultValue: '',
    allowSystemLevel: true,
    allowProfileLevel: true,
    validationRules: '',
    displayOrder: 0
  });

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingDefinitionApi.getAll(showInactive);
      setDefinitions(data);
    } catch (err) {
      console.error('Error fetching definitions:', err);
      setError('Feil ved henting av innstillingsdefinisjoner');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Inkluder inaktive: gruppeoverskrifter og admin-panelet trenger navn på alt som finnes
      setCategories(await settingDefinitionApi.getCategories(true));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchDefinitions();
  }, [showInactive]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const activeCategories = categories.filter(c => c.isActive);
  const categoryLabel = (key: string) =>
    categories.find(c => c.categoryKey === key)?.displayName || key;

  // Filter definitions
  const filteredDefinitions = definitions.filter(def => {
    const matchesCategory = selectedCategory === 'all' || def.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      def.settingKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
      def.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (def.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    return matchesCategory && matchesSearch;
  });

  // Group by category for display
  const groupedDefinitions = filteredDefinitions.reduce((acc, def) => {
    if (!acc[def.category]) {
      acc[def.category] = [];
    }
    acc[def.category].push(def);
    return acc;
  }, {} as Record<string, SettingDefinition[]>);

  const handleEditStart = (def: SettingDefinition) => {
    setEditingKey(def.settingKey);
    setEditFormData({
      displayName: def.displayName,
      description: def.description || '',
      category: def.category,
      dataType: def.dataType,
      defaultValue: def.defaultValue || '',
      allowSystemLevel: def.allowSystemLevel,
      allowProfileLevel: def.allowProfileLevel,
      validationRules: def.validationRules || '',
      displayOrder: def.displayOrder,
      isActive: def.isActive
    });
  };

  const handleEditCancel = () => {
    setEditingKey(null);
    setEditFormData(null);
  };

  const handleEditSave = async () => {
    if (!editingKey || !editFormData) return;

    try {
      await settingDefinitionApi.update(editingKey, editFormData);
      setEditingKey(null);
      setEditFormData(null);
      setSuccessMessage('Innstillingen ble oppdatert');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchDefinitions();
    } catch (err) {
      console.error('Error updating definition:', err);
      setError('Feil ved oppdatering av innstilling');
    }
  };

  const handleCreateStart = () => {
    setIsCreating(true);
    setNewDefinition({
      settingKey: '',
      displayName: '',
      description: '',
      category: 'general',
      dataType: 'String',
      defaultValue: '',
      allowSystemLevel: true,
      allowProfileLevel: true,
      validationRules: '',
      displayOrder: 0
    });
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
  };

  const handleCreateSave = async () => {
    try {
      await settingDefinitionApi.create(newDefinition);
      setIsCreating(false);
      setSuccessMessage('Ny innstilling ble opprettet');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchDefinitions();
    } catch (err: any) {
      console.error('Error creating definition:', err);
      setError(err.response?.data?.message || 'Feil ved opprettelse av innstilling');
    }
  };

  const handleCategoryCreate = async () => {
    try {
      await settingDefinitionApi.createCategory(newCategory);
      setNewCategory({ categoryKey: '', displayName: '', displayOrder: 100, isActive: true });
      setSuccessMessage('Kategorien ble opprettet');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error creating category:', err);
      setError(err.response?.data?.message || 'Feil ved opprettelse av kategori');
    }
  };

  const handleCategorySave = async () => {
    if (!editingCategoryKey || !editCategory) return;
    try {
      await settingDefinitionApi.updateCategory(editingCategoryKey, editCategory);
      setEditingCategoryKey(null);
      setEditCategory(null);
      setSuccessMessage('Kategorien ble oppdatert');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error updating category:', err);
      setError(err.response?.data?.message || 'Feil ved oppdatering av kategori');
    }
  };

  const handleToggleActive = async (def: SettingDefinition) => {
    try {
      if (def.isActive) {
        await settingDefinitionApi.deactivate(def.settingKey);
        setSuccessMessage('Innstillingen ble deaktivert');
      } else {
        await settingDefinitionApi.activate(def.settingKey);
        setSuccessMessage('Innstillingen ble aktivert');
      }
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchDefinitions();
    } catch (err) {
      console.error('Error toggling active state:', err);
      setError('Feil ved endring av status');
    }
  };

  const handleDelete = async (settingKey: string) => {
    if (!window.confirm('Er du sikker på at du vil slette denne innstillingen permanent?')) {
      return;
    }

    try {
      await settingDefinitionApi.delete(settingKey);
      setSuccessMessage('Innstillingen ble slettet');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchDefinitions();
    } catch (err) {
      console.error('Error deleting definition:', err);
      setError('Feil ved sletting av innstilling');
    }
  };

  if (loading && definitions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Laster innstillinger...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Innstillingsdefinisjoner</h2>
        <div className="space-x-2">
          <button
            onClick={() => setShowCategoryAdmin(!showCategoryAdmin)}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
          >
            Kategorier
          </button>
          <button
            onClick={handleCreateStart}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            + Ny innstilling
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold">&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-md flex flex-wrap gap-4 items-center">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Alle kategorier</option>
            {activeCategories.map(cat => (
              <option key={cat.categoryKey} value={cat.categoryKey}>{cat.displayName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Søk</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Søk i nøkkel, navn eller beskrivelse..."
            className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center mt-6">
          <input
            type="checkbox"
            id="showInactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300"
          />
          <label htmlFor="showInactive" className="ml-2 text-sm text-gray-700">
            Vis inaktive
          </label>
        </div>
        <div className="ml-auto text-sm text-gray-500">
          {filteredDefinitions.length} av {definitions.length} innstillinger
        </div>
      </div>

      {/* Kategori-administrasjon */}
      {showCategoryAdmin && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
          <h3 className="font-semibold text-amber-800 mb-1">Kategorikatalog</h3>
          <p className="text-sm text-gray-600 mb-3">
            Alle innstillinger må bruke en kategori herfra (håndheves også i databasen).
            Nøkkelen kan ikke endres etter opprettelse — kun visningsnavn, rekkefølge og aktiv-status.
          </p>
          <table className="w-full bg-white border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nøkkel</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Visningsnavn</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rekkefølge</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Aktiv</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map(cat => (
                <tr key={cat.categoryKey} className={!cat.isActive ? 'opacity-60' : ''}>
                  {editingCategoryKey === cat.categoryKey && editCategory ? (
                    <>
                      <td className="px-3 py-2 text-sm font-mono text-gray-600">{cat.categoryKey}</td>
                      <td className="px-3 py-2">
                        <input type="text" value={editCategory.displayName}
                          onChange={(e) => setEditCategory({ ...editCategory, displayName: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" value={editCategory.displayOrder}
                          onChange={(e) => setEditCategory({ ...editCategory, displayOrder: parseInt(e.target.value) || 0 })}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={editCategory.isActive}
                          onChange={(e) => setEditCategory({ ...editCategory, isActive: e.target.checked })}
                          className="h-4 w-4 text-blue-600 rounded" />
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        <button onClick={handleCategorySave} className="text-green-600 hover:text-green-800 text-sm font-medium">Lagre</button>
                        <button onClick={() => { setEditingCategoryKey(null); setEditCategory(null); }} className="text-gray-600 hover:text-gray-800 text-sm font-medium">Avbryt</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-sm font-mono text-gray-600">{cat.categoryKey}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{cat.displayName}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{cat.displayOrder}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${cat.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {cat.isActive ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => { setEditingCategoryKey(cat.categoryKey); setEditCategory({ ...cat }); }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium">Rediger</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {/* Ny kategori */}
              <tr className="bg-gray-50">
                <td className="px-3 py-2">
                  <input type="text" value={newCategory.categoryKey}
                    onChange={(e) => setNewCategory({ ...newCategory, categoryKey: e.target.value })}
                    placeholder="ny-kategori (kebab-case)"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={newCategory.displayName}
                    onChange={(e) => setNewCategory({ ...newCategory, displayName: e.target.value })}
                    placeholder="Visningsnavn"
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="number" value={newCategory.displayOrder}
                    onChange={(e) => setNewCategory({ ...newCategory, displayOrder: parseInt(e.target.value) || 100 })}
                    className="w-24 border border-gray-300 rounded px-2 py-1 text-sm" />
                </td>
                <td className="px-3 py-2 text-center text-sm text-gray-400">Aktiv</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={handleCategoryCreate}
                    disabled={!newCategory.categoryKey || !newCategory.displayName}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400">
                    + Legg til
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Create Form */}
      {isCreating && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
          <h3 className="font-semibold text-blue-800 mb-4">Ny innstillingsdefinisjon</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nøkkel *</label>
              <input
                type="text"
                value={newDefinition.settingKey}
                onChange={(e) => setNewDefinition({...newDefinition, settingKey: e.target.value})}
                placeholder="kategori.innstillingsnavn"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">F.eks. picking.confirmBeforeComplete</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Visningsnavn *</label>
              <input
                type="text"
                value={newDefinition.displayName}
                onChange={(e) => setNewDefinition({...newDefinition, displayName: e.target.value})}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">Beskrivelse</label>
              <textarea
                value={newDefinition.description}
                onChange={(e) => setNewDefinition({...newDefinition, description: e.target.value})}
                rows={2}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Kategori</label>
              <select
                value={newDefinition.category}
                onChange={(e) => setNewDefinition({...newDefinition, category: e.target.value})}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {activeCategories.map(cat => (
                  <option key={cat.categoryKey} value={cat.categoryKey}>{cat.displayName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Datatype</label>
              <select
                value={newDefinition.dataType}
                onChange={(e) => setNewDefinition({...newDefinition, dataType: e.target.value as SettingDataType})}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {SETTING_DATA_TYPES.map(type => (
                  <option key={type} value={type}>{DATA_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Standardverdi</label>
              <input
                type="text"
                value={newDefinition.defaultValue}
                onChange={(e) => setNewDefinition({...newDefinition, defaultValue: e.target.value})}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sorteringsrekkefølge</label>
              <input
                type="number"
                value={newDefinition.displayOrder}
                onChange={(e) => setNewDefinition({...newDefinition, displayOrder: parseInt(e.target.value) || 0})}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Valideringsregler (JSON)</label>
              <input
                type="text"
                value={newDefinition.validationRules}
                onChange={(e) => setNewDefinition({...newDefinition, validationRules: e.target.value})}
                placeholder='{"min": 0, "max": 100}'
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-center space-x-4 mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newDefinition.allowSystemLevel}
                  onChange={(e) => setNewDefinition({...newDefinition, allowSystemLevel: e.target.checked})}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Systemnivå</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newDefinition.allowProfileLevel}
                  onChange={(e) => setNewDefinition({...newDefinition, allowProfileLevel: e.target.checked})}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Profilnivå</span>
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={handleCreateCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleCreateSave}
              disabled={!newDefinition.settingKey || !newDefinition.displayName}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Opprett
            </button>
          </div>
        </div>
      )}

      {/* Definitions Table */}
      {Object.entries(groupedDefinitions).map(([category, defs]) => (
        <div key={category} className="border border-gray-200 rounded-md overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 font-semibold text-gray-700">
            {categoryLabel(category)} ({defs.length})
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nøkkel</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Visningsnavn</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Standard</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">System</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Profil</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {defs.map((def) => (
                <tr key={def.settingKey} className={`hover:bg-gray-50 ${!def.isActive ? 'bg-red-50 opacity-60' : ''}`}>
                  {editingKey === def.settingKey && editFormData ? (
                    // Edit mode
                    <>
                      <td className="px-4 py-2 text-sm font-mono text-gray-600">{def.settingKey}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={editFormData.displayName}
                          onChange={(e) => setEditFormData({...editFormData, displayName: e.target.value})}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={editFormData.dataType}
                          onChange={(e) => setEditFormData({...editFormData, dataType: e.target.value as SettingDataType})}
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          {SETTING_DATA_TYPES.map(type => (
                            <option key={type} value={type}>{DATA_TYPE_LABELS[type]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={editFormData.defaultValue}
                          onChange={(e) => setEditFormData({...editFormData, defaultValue: e.target.value})}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={editFormData.allowSystemLevel}
                          onChange={(e) => setEditFormData({...editFormData, allowSystemLevel: e.target.checked})}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={editFormData.allowProfileLevel}
                          onChange={(e) => setEditFormData({...editFormData, allowProfileLevel: e.target.checked})}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={editFormData.isActive}
                          onChange={(e) => setEditFormData({...editFormData, isActive: e.target.checked})}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <button
                          onClick={handleEditSave}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Lagre
                        </button>
                        <button
                          onClick={handleEditCancel}
                          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                        >
                          Avbryt
                        </button>
                      </td>
                    </>
                  ) : (
                    // View mode
                    <>
                      <td className="px-4 py-2 text-sm font-mono text-gray-600">
                        {def.settingKey}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">{def.displayName}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{def.description || <span className="text-gray-300 italic">Ingen beskrivelse</span>}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{DATA_TYPE_LABELS[def.dataType]}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                        {def.defaultValue || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {def.allowSystemLevel ? (
                          <span className="text-green-600">&#10003;</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {def.allowProfileLevel ? (
                          <span className="text-green-600">&#10003;</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          def.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {def.isActive ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <button
                          onClick={() => handleEditStart(def)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Rediger
                        </button>
                        <button
                          onClick={() => handleToggleActive(def)}
                          className={`text-sm font-medium ${
                            def.isActive ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'
                          }`}
                        >
                          {def.isActive ? 'Deaktiver' : 'Aktiver'}
                        </button>
                        <button
                          onClick={() => handleDelete(def.settingKey)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Slett
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {filteredDefinitions.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Ingen innstillinger funnet med gjeldende filter
        </div>
      )}
    </div>
  );
};

export default SettingDefinitionManagement;
